/**
 * Central conversion tracking for TyreLink.
 *
 * Fires to Meta Pixel (fbq) and GA4 (gtag) where both are relevant.
 * Every call is defensive: if a script is blocked (ad blockers are common)
 * or hasn't loaded yet, tracking silently no-ops and NEVER breaks the app.
 *
 * Events we send and why:
 *   PageView            — automatic (in index.html); builds retargeting audiences
 *   Lead                — user starts the sign-up flow; mid-funnel signal
 *   CompleteRegistration— user finishes registering; THE conversion Meta optimises for
 *   Search              — user searches tyres/rims; strong intent, feeds lookalikes
 *   Contact             — user opens the WhatsApp CTA (competition page)
 */

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    gtag?: (...args: any[]) => void
  }
}

type Params = Record<string, string | number | boolean | undefined>

function fb(event: string, params?: Params) {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', event, params)
    }
  } catch {
    /* tracking must never break the app */
  }
}

function ga(event: string, params?: Params) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', event, params)
    }
  } catch {
    /* tracking must never break the app */
  }
}

/** User began the sign-up flow (picked a method in the chat). */
export function trackSignUpStarted(method: 'whatsapp' | 'email') {
  fb('Lead', { content_name: 'signup_started', method })
  ga('sign_up_started', { method })
}

/** User completed registration. This is the primary conversion. */
export function trackRegistrationComplete(method: 'whatsapp' | 'email') {
  fb('CompleteRegistration', {
    content_name: 'tyrelink_registration',
    method,
    status: true,
  })
  ga('sign_up', { method })
}

/** User searched for tyres or rims. */
export function trackSearch(searchTerm: string, type: 'tyre' | 'rim' | 'general' = 'general') {
  fb('Search', { search_string: searchTerm, content_category: type })
  ga('search', { search_term: searchTerm, content_category: type })
}

/** User tapped a WhatsApp call-to-action (e.g. competition page button). */
export function trackWhatsAppCTA(source: string) {
  fb('Contact', { content_name: 'whatsapp_cta', source })
  ga('whatsapp_cta_click', { source })
}

/** Page view on SPA route change (initial load is handled in index.html). */
export function trackPageView(path: string) {
  fb('PageView')
  ga('page_view', { page_path: path })
}
